import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Badge } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const vTone = { verified: "green", pending: "amber", needs_review: "amber", suspended: "red", blocked: "red", inactive: "gray" };
export const mTone = { active: "green", inactive: "gray", canceled: "red", past_due: "amber", suspended: "red" };
export const fmtJoined = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d)) return "—";
    return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} · ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
};

const ID_TYPE_LABEL = { drivers_license: "Driver's License", state_id: "State ID", passport: "Passport" };

// Shared admin member profile dialog — full info + verify/suspend/block actions.
export default function MemberDialog({ userId, onClose }) {
    const qc = useQueryClient();
    const [note, setNote] = useState("");
    const { data } = useQuery({ queryKey: ["admin-member", userId], queryFn: async () => (await api.get(`/admin/members/${userId}`)).data, enabled: !!userId });

    const act = async (fn, msg) => { try { await fn(); toast.success(msg); qc.invalidateQueries(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
    const setStatus = (body) => act(() => api.put(`/admin/members/${userId}/status`, body), "Member updated");
    const setMembership = (status) => act(() => api.put(`/admin/members/${userId}/membership`, { status }), "Membership updated");
    const addNote = () => act(async () => { await api.post(`/admin/members/${userId}/notes`, { note }); setNote(""); }, "Note added");
    const viewDoc = async (docId) => {
        try {
            const res = await api.get(`/admin/id-documents/${docId}/file`, { responseType: "blob" });
            window.open(URL.createObjectURL(res.data), "_blank");
        } catch (e) { toast.error("Could not load document."); }
    };

    if (!userId) return null;
    if (!data) return null;
    const u = data.user;
    return (
        <Dialog open={!!userId} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto" data-testid="admin-member-dialog">
                <DialogHeader><DialogTitle>{u.first_name} {u.last_name}</DialogTitle><DialogDescription>Full member profile and account actions.</DialogDescription></DialogHeader>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <div><span className="text-zinc-500">Email:</span> {u.email}</div>
                    <div><span className="text-zinc-500">Phone:</span> {u.phone || "—"}</div>
                    <div><span className="text-zinc-500">DOB:</span> {u.date_of_birth || "—"}</div>
                    <div><span className="text-zinc-500">Location:</span> {u.city}, {u.state} {u.zip_code}</div>
                    <div><span className="text-zinc-500">Joined:</span> {fmtJoined(u.created_at)}</div>
                    <div><span className="text-zinc-500">Verification:</span> <Badge tone={vTone[u.verification_status]}>{u.verification_status}</Badge></div>
                    <div><span className="text-zinc-500">Membership:</span> <Badge tone={mTone[data.membership?.status] || "gray"}>{data.membership?.status || "none"}</Badge></div>
                    <div><span className="text-zinc-500">Outings:</span> {data.activity_count}</div>
                    <div><span className="text-zinc-500">No-shows:</span> {data.no_shows} · Late cancels: {data.late_cancellations}</div>
                    <div><span className="text-zinc-500">Reservation warnings:</span> {u.reservation_warnings || 0} of 2</div>
                </div>

                <div className="mt-2 flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => setStatus({ verification_status: "verified", account_status: "active" })} data-testid="admin-verify">Approve / Verify</Button>
                    <Button size="sm" variant="outline" onClick={() => setStatus({ verification_status: "needs_review" })}>Needs Review</Button>
                    <Button size="sm" variant="outline" onClick={() => setStatus({ account_status: "suspended", verification_status: "suspended" })} data-testid="admin-suspend">Suspend</Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => setStatus({ verification_status: "blocked", account_status: "suspended" })} data-testid="admin-block">Block</Button>
                    <Button size="sm" variant="outline" onClick={() => setStatus({ verification_status: "verified", account_status: "active" })}>Reactivate</Button>
                    <Button size="sm" variant="outline" data-testid="admin-reset-warnings" onClick={async () => { try { await api.post(`/admin/members/${userId}/reset-warnings`); toast.success("Warnings cleared & account reactivated."); window.location.reload(); } catch (e) { toast.error("Failed to reset warnings."); } }}>Reset Warnings</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="secondary" onClick={() => setMembership("active")}>Mark Sub Active</Button>
                    <Button size="sm" variant="secondary" onClick={() => setMembership("inactive")}>Mark Sub Inactive</Button>
                </div>

                {data.reports?.length > 0 && <div className="rounded bg-red-50 p-3 text-sm text-red-700">{data.reports.length} report(s) filed against this member.</div>}
                {data.restrictions?.length > 0 && <div className="rounded bg-amber-50 p-3 text-sm text-amber-700">{data.restrictions.length} matching restriction(s).</div>}

                <div>
                    <p className="mb-2 text-sm font-600">Identity documents</p>
                    {(!data.id_documents || data.id_documents.length === 0) ? (
                        <div className="rounded bg-zinc-100 px-3 py-2 text-sm text-zinc-500" data-testid="admin-no-id-docs">No ID uploaded yet.</div>
                    ) : (
                        <div className="space-y-2">
                            {data.id_documents.map((d) => (
                                <div key={d.id} data-testid={`admin-id-doc-${d.id}`} className="flex items-center justify-between rounded border border-zinc-200 bg-white px-3 py-2 text-sm">
                                    <div>
                                        <span className="font-600">{ID_TYPE_LABEL[d.doc_type] || d.doc_type}</span>
                                        <span className="ml-2 text-zinc-400">{d.original_filename}</span>
                                    </div>
                                    <Button size="sm" variant="outline" data-testid={`admin-view-id-${d.id}`} onClick={() => viewDoc(d.id)}>View ID</Button>
                                </div>
                            ))}
                            <div className="flex gap-2 pt-1">
                                <Button size="sm" data-testid="admin-approve-id" onClick={() => setStatus({ verification_status: "verified", account_status: "active" })}>Approve ID & Verify</Button>
                                <Button size="sm" variant="outline" className="text-red-600" data-testid="admin-reject-id" onClick={() => setStatus({ verification_status: "pending" })}>Reject ID</Button>
                            </div>
                        </div>
                    )}
                </div>

                <div>
                    <p className="mb-2 text-sm font-600">Internal notes</p>
                    <div className="space-y-1">{(data.notes || []).map((n) => <div key={n.id} className="rounded bg-zinc-100 px-3 py-2 text-sm">{n.note} <span className="text-xs text-zinc-400">— {n.author}</span></div>)}</div>
                    <div className="mt-2 flex gap-2">
                        <Textarea data-testid="admin-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add an internal note…" />
                        <Button onClick={addNote} data-testid="admin-note-add">Add</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
